import { Megaphone } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import LayoutAction from "../../actions/LayoutAction";
import { Button } from "../../components/ui";
import type { GtmPlayStatus } from "../api/GtmApi";
import GtmEmptyState from "../components/GtmEmptyState";
import GtmSessionBanner from "../components/GtmSessionBanner";
import { GTM_FIND_LEADS_NO_INVITES } from "../disclosureCopy";
import { useGtmBrainQuery } from "../hooks/useGtmBrain";
import { useGtmPlayCreateMutation, useGtmPlaysQuery } from "../hooks/useGtmPlays";
import { useGtmSessionQuery } from "../hooks/useGtmSession";
import PlaySuggestionsPanel from "../plays/PlaySuggestionsPanel";
import {
  buildPlaySuggestions,
  type PlaySuggestion,
} from "../plays/playSuggestions";

type StatusFilter = "all" | GtmPlayStatus;

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "draft", label: "Draft" },
];

function statusPillClass(status: GtmPlayStatus) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "paused":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function GtmPlaysPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<StatusFilter>("all");
  const [usingSuggestionId, setUsingSuggestionId] = useState<string | null>(null);

  const playsQuery = useGtmPlaysQuery(projectId);
  const brainQuery = useGtmBrainQuery(projectId);
  const sessionQuery = useGtmSessionQuery(projectId);
  const createMutation = useGtmPlayCreateMutation(projectId);

  const plays = playsQuery.data || [];
  const brain = brainQuery.data?.brain ?? null;
  const docs = brainQuery.data?.docs || [];
  const hasBrain = !!brain;
  const session = sessionQuery.data ?? null;
  const loading = playsQuery.isLoading || brainQuery.isLoading || sessionQuery.isLoading;

  const suggestions = useMemo(
    () => buildPlaySuggestions({ brain, docs, plays }),
    [brain, docs, plays]
  );

  const newPlayHref = `/workspaces/${projectId}/gtm/leads/plays/new?key=Leads`;

  useEffect(() => {
    document.title = "GTM · Plays";
  }, []);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
      navigate(newPlayHref, { replace: true });
    }
  }, [navigate, newPlayHref, searchParams, setSearchParams]);

  useEffect(() => {
    if (!playsQuery.isError) return;
    const err: any = playsQuery.error;
    dispatch(
      LayoutAction.setNotification({
        title: t("Plays"),
        type: "error",
        message: err?.response?.data?.message || err?.message || t("Failed to load plays"),
      }) as any
    );
  }, [dispatch, playsQuery.error, playsQuery.isError, t]);

  const filtered = useMemo(() => {
    if (filter === "all") return plays;
    return plays.filter((p) => p.status === filter);
  }, [filter, plays]);

  const openCreate = () => {
    if (!hasBrain) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Plays"),
          type: "error",
          message: t("Build a GTM brain before creating a play"),
        }) as any
      );
      navigate(`/workspaces/${projectId}/gtm/leads/brain?key=Leads`);
      return;
    }
    navigate(newPlayHref);
  };

  const onUseSuggestion = async (suggestion: PlaySuggestion) => {
    if (!hasBrain) {
      navigate(`/workspaces/${projectId}/gtm/leads/brain?key=Leads`);
      return;
    }
    try {
      setUsingSuggestionId(suggestion.id);
      const play = await createMutation.mutateAsync({
        audience: suggestion.audience,
        name: suggestion.name,
      });
      dispatch(
        LayoutAction.setNotification({
          title: t("Plays"),
          type: "success",
          message: play.angle
            ? t("Play created")
            : t("Play created (angle pending — you can edit it later)"),
        }) as any
      );
      navigate(`/workspaces/${projectId}/gtm/leads/plays/${play.id}?key=Leads`);
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("New play"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Failed to create play"),
        }) as any
      );
    } finally {
      setUsingSuggestionId(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{t("Plays")}</h1>
          <p className="text-sm text-gray-500">
            {t("Audience ideas and LinkedIn lead sequences for this workspace.")}
          </p>
        </div>
        <Button type="button" variant="primary" size="sm" onClick={openCreate}>
          {t("New play")}
        </Button>
      </div>

      {plays.length > 0 ? (
        <div className="flex gap-1 border-b border-gray-100 px-4">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setFilter(item.id)}
              className={`border-b-2 px-3 py-2 text-sm font-medium ${
                filter === item.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        <div className="mb-4">
          <GtmSessionBanner projectId={projectId} session={session} showPausedNote={false} />
        </div>
        {loading ? (
          <p className="text-sm text-gray-500">{t("Loading…")}</p>
        ) : (
          <div className="space-y-8">
            {hasBrain && suggestions.length > 0 ? (
              <PlaySuggestionsPanel
                suggestions={suggestions}
                usingId={usingSuggestionId}
                onUse={onUseSuggestion}
              />
            ) : null}

            {plays.length === 0 ? (
              suggestions.length === 0 ? (
                <div>
                  <GtmEmptyState
                    icon={Megaphone}
                    title={hasBrain ? "No plays yet" : "Build your brain first"}
                    description={
                      hasBrain
                        ? "Create a play with an audience. We suggest an angle from your brain and attach the default LinkedIn sequence."
                        : "Submit a company website on Brain, then come back to start a play."
                    }
                    footnote={GTM_FIND_LEADS_NO_INVITES}
                  />
                  <div className="px-4 pb-6 text-center text-xs text-gray-400">
                    <Link
                      className="text-blue-600 hover:underline"
                      to={`/workspaces/${projectId}/gtm/leads/brain?key=Leads`}
                    >
                      {t("Go to Brain")}
                    </Link>
                    {" · "}
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={openCreate}
                    >
                      {t("New play")}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  {t("Or describe a custom audience with New play.")}
                </p>
              )
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-500">{t("No plays in this filter.")}</p>
            ) : (
              <div>
                {suggestions.length > 0 ? (
                  <h2 className="mb-3 text-sm font-semibold text-slate-800">{t("Your plays")}</h2>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((play) => (
                    <Link
                      key={play.id}
                      to={`/workspaces/${projectId}/gtm/leads/plays/${play.id}?key=Leads`}
                      className="block rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:bg-blue-50/40"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h2 className="line-clamp-2 text-sm font-semibold text-gray-900">
                          {play.name}
                        </h2>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusPillClass(
                            play.status
                          )}`}
                        >
                          {play.status}
                        </span>
                      </div>
                      <p className="mt-2 line-clamp-3 text-sm text-gray-600">
                        {play.angle || play.audience || t("No angle yet")}
                      </p>
                      <p className="mt-3 text-xs font-medium text-blue-600">{t("Open play →")}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
